import { ShieldCheck, ClipboardCheck, MapPin, HeartHandshake } from 'lucide-react';
import Reveal, { easeOut } from './Reveal';
import { motion } from 'framer-motion';
import Gallery from './Gallery';

const GALLERY = [
  {
    src: '/why-team.jpg',
    alt: 'Support worker and client smiling together outdoors',
    pos: 'object-[52%_50%] lg:object-center',
  },
  {
    src: '/gallery-agreement.webp',
    alt: 'Signing a service agreement with a Care For All client',
    pos: 'object-[62%_50%]',
  },
  {
    src: '/gallery-walk.jpg',
    alt: 'Walking a rainforest trail on a community outing',
    pos: 'object-center',
  },
  {
    src: '/gallery-music.jpg',
    alt: 'Live music on a community outing in Mareeba',
    pos: 'object-[50%_0%]',
  },
];

const POINTS = [
  {
    icon: ShieldCheck,
    title: 'Registered NDIS Provider',
    desc: 'Fully registered with the NDIS Quality & Safeguards Commission.',
  },
  {
    icon: ClipboardCheck,
    title: 'Audit-Approved',
    desc: 'Our practices meet NDIS practice standards — verified through audit.',
  },
  {
    icon: MapPin,
    title: 'Local & Responsive',
    desc: 'Based right here in Mareeba, on the road across the Tablelands every day.',
  },
  {
    icon: HeartHandshake,
    title: 'Person-Centred',
    desc: 'Support shaped around your goals, your choices and your pace.',
  },
];

const VALUES = [
  { word: 'Empowering', line: 'We build skills and confidence, not dependence.' },
  { word: 'Supporting', line: 'Someone in your corner, every step of the way.' },
  { word: 'Inclusive', line: 'Everyone belongs, everyone is respected.' },
];

export default function WhyUs() {
  return (
    <section id="why-us" className="relative bg-white py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-sky-700">Why Care For All</p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900 leading-[1.08]">
              Local care, registered and{' '}
              <span className="font-accent italic font-normal text-azure">audit-approved</span>
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Care For All – Support Services is a registered NDIS provider based right here in Mareeba.
              Every support we deliver meets NDIS practice standards — so you can trust the care in our hands.
            </p>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                data-testid={`why-point-${i}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.09, ease: easeOut }}
                className="flex gap-4 rounded-2xl border border-sky-100 bg-brand-cream/70 p-5 hover:shadow-soft hover:-translate-y-1 transition-all duration-300"
              >
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shrink-0">
                  <p.icon size={19} />
                </span>
                <div>
                  <p className="font-display font-bold text-slate-900">{p.title}</p>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <Reveal delay={0.1}>
            <img
              src="/ndis-registered.png"
              alt="I love NDIS — Registered NDIS Provider"
              data-testid="ndis-badge-row"
              className="mt-8 h-14 w-auto"
            />
          </Reveal>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-sky-100 pt-8">
            {VALUES.map((v, i) => (
              <Reveal key={v.word} delay={i * 0.1}>
                <p className="font-display font-extrabold text-xl text-navy tracking-tight">
                  {v.word}
                  <span className="block mt-1 h-1 w-8 rounded-full bg-gradient-to-r from-sky-500 to-cyan-400" />
                </p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.line}</p>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15} className="relative">
          <div className="relative overflow-hidden rounded-[2.5rem] shadow-lift">
            <Gallery images={GALLERY} />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/45 via-transparent to-transparent" />
            <img
              src="/ndis-logo.png"
              alt="NDIS"
              data-testid="why-photo-ndis"
              className="pointer-events-none absolute top-3 right-5 z-10 h-9 w-auto rounded-xl shadow-lift"
            />
          </div>
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -bottom-5 left-6 lg:left-10 flex items-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-lift border border-sky-100"
            data-testid="why-badge-audit"
          >
            <span className="grid place-items-center h-10 w-10 rounded-full bg-emerald-100 text-emerald-600">
              <ClipboardCheck size={18} />
            </span>
            <div>
              <p className="font-display font-bold text-slate-900 text-sm">Audit-Approved</p>
              <p className="text-xs text-slate-500">NDIS practice standards</p>
            </div>
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
}
